import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiMenu, FiX, FiPhone } from 'react-icons/fi';
import { LuMountain } from 'react-icons/lu';
import colors from '../../styles/colors';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 15px 0;
  transition: all 0.4s ease;
  background: ${props => props.$scrolled ? 'rgba(26, 58, 26, 0.98)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(20px)' : 'none'};
  box-shadow: ${props => props.$scrolled ? '0 4px 30px rgba(0, 0, 0, 0.2)' : 'none'};
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${colors.gradient.gold};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: ${colors.primary.dark};
  box-shadow: ${colors.shadowGold};
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;

  @media (max-width: 500px) {
    display: none;
  }
`;

const LogoMain = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${colors.text.white};
  letter-spacing: -0.5px;
`;

const LogoSub = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${colors.accent.gold};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 35px;

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    right: ${props => props.$open ? '0' : '-100%'};
    width: 300px;
    height: 100vh;
    background: ${colors.primary.dark};
    flex-direction: column;
    justify-content: center;
    gap: 30px;
    transition: right 0.3s ease;
    box-shadow: ${props => props.$open ? '-10px 0 30px rgba(0,0,0,0.3)' : 'none'};
  }
`;

const NavLink = styled.a`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${colors.text.light};
  cursor: pointer;
  position: relative;
  padding: 5px 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${colors.accent.gold};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.accent.gold};
    &::after {
      width: 100%;
    }
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 1000px) {
    display: none;
  }
`;

const PhoneLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.text.light};
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none !important;

  svg {
    color: ${colors.accent.gold};
  }

  &:hover {
    color: ${colors.accent.gold};
  }
`;

const CTAButton = styled.a`
  padding: 12px 28px;
  background: ${colors.gradient.gold};
  color: ${colors.primary.dark};
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 50px;
  box-shadow: ${colors.shadowGold};
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(201, 162, 39, 0.4);
  }
`;

const MobileButton = styled.button`
  display: none;
  background: none;
  color: ${colors.text.white};
  font-size: 1.5rem;
  z-index: 1001;

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 25px;
  right: 25px;
  background: none;
  color: ${colors.text.white};
  font-size: 1.5rem;

  @media (max-width: 900px) {
    display: block;
  }
`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <HeaderContainer $scrolled={scrolled}>
      <Nav>
        <Logo onClick={() => scrollTo('hero')}>
          <LogoIcon><LuMountain /></LogoIcon>
          <LogoText>
            <LogoMain>Greenridge</LogoMain>
            <LogoSub>Landscaping Co.</LogoSub>
          </LogoText>
        </Logo>

        <NavCenter $open={menuOpen}>
          <CloseButton onClick={() => setMenuOpen(false)}>
            <FiX />
          </CloseButton>
          <NavLink onClick={() => scrollTo('services')}>Services</NavLink>
          <NavLink onClick={() => scrollTo('about')}>About</NavLink>
          <NavLink onClick={() => scrollTo('work')}>Our Work</NavLink>
          <NavLink onClick={() => scrollTo('contact')}>Contact</NavLink>
        </NavCenter>

        <NavRight>
          <PhoneLink href="tel:5559876543">
            <FiPhone /> (555) 987-6543
          </PhoneLink>
          <CTAButton onClick={() => scrollTo('contact')}>
            Free Estimate
          </CTAButton>
        </NavRight>

        <MobileButton onClick={() => setMenuOpen(true)}>
          <FiMenu />
        </MobileButton>
      </Nav>
    </HeaderContainer>
  );
}
